import { Grid, IconButton, Link, withTheme } from '@material-ui/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import moment from '../../moment-hawaii';
import ImageSlider from '../ImageSlider';
import {
    bookerFieldsFromTickets,
    getBookerFieldDefaults,
    getBookingGuestData,
    withGuestStayDates,
    formatMoney
} from '../../utils';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import { useSwaggerClient } from '../../hooks/useSwaggerClient';
import Calendar from '../calendar';
import ReadonlyEditor from '../drafteditor/components/ReadonlyEditor';
import { ChevronLeftIcon } from '../../icons';
import { convertToFinalPrice } from '../../finalPrice';
import { useGA } from '../../hooks/useGoogleAnalytics';
import SingleAgentCommissionTable from './SingleAgentCommissionTable';
import ActivityForm from './ActivityForm';
import { CancellationWindowText } from '../../pages/payment/components/CancellationPolicy';
import ActivityActionButtons from '../../pages/activitycards/components/ActivityActionButtons';

/**
 * Create a guest object with default values from booking_fields
 * @param {Array} bookingFields - Array of booking field definitions
 * @returns {Object} Guest object with default values set by field.id
 */
function createGuestWithDefaults(bookingFields) {
    if (!bookingFields || bookingFields.length === 0) {
        return {};
    }

    const guest = {};
    bookingFields.forEach(field => {
        if (
            field.defaultValue !== undefined &&
            field.defaultValue !== null &&
            field.defaultValue !== ''
        ) {
            guest[field.id] = field.defaultValue;
        }
    });
    return guest;
}

/**
 * Create an array of guests with default values
 * @param {number} count - Number of guests
 * @param {Array} bookingFields - Array of booking field definitions
 * @returns {Array} Array of guest objects with defaults
 */
function createGuestsWithDefaults(count, bookingFields) {
    const template = createGuestWithDefaults(bookingFields);
    // Create separate object instances for each guest
    return Array(count)
        .fill(null)
        .map(() => ({ ...template }));
}

const SingleActivity = ({
    activityId,
    checkInDate,
    groupSize,
    onNavigateBack,
    theme,
    fillCart,
    updateBookingGuest,
    customer,
    user
}) => {
    // On by default: an agent adding tickets is choosing by what they earn.
    const [showCommission, setShowCommission] = useState(true);
    const [showTicketSellingRanges, setShowTicketSellingRanges] = useState(true);

    const [activity, activityLoading] = useSwaggerClient('activities', 'getActivityById', {
        activityId
    });

    const [calendarWeekStart, setCalendarWeekStart] = useState(checkInDate);
    const history = useHistory();
    const { showModal } = useModals();

    const [selectedTimeslot, selectTimeslot] = useState(false);
    // IMPORTANT: user?.role is added to trigger an api call when the role changes
    // this is required due to min_booking_window, it's not active in case of agents
    const [timeslotsRaw, timeslotsLoading, _error, _setData, reloadTimeslots] = useSwaggerClient(
        !calendarWeekStart ? false : 'activities',
        'getTimeslotsForActivity',
        {
            activityId,
            fromDate: calendarWeekStart.format('YYYY-MM-DD'),
            toDate: calendarWeekStart
                .clone()
                .add(7, 'days')
                .format('YYYY-MM-DD'),
            groupSize
        },
        // to trigger rerender on role change
        [user?.role]
    );

    const timeslots = useMemo(() => {
        if (!timeslotsRaw) {
            return null;
        }
        return timeslotsRaw.timeslots.map(e => {
            let seats = '';
            let extraInfo = '';

            let isSeatEvent = false;
            const seatsDiff = (e?.seats_available ?? 0) - (e?.seats_sold ?? 0);
            const availableSeats = seatsDiff > 0 ? seatsDiff : 0;

            if (typeof e.seats_available !== 'undefined') {
                isSeatEvent = true;
                if (user?.role === 'agent' || user?.role === 'admin') {
                    // if overbooked, show the overbooked amount in the title
                    if (e.seats_sold > e.seats_available) {
                        seats = ` (overbooked by ${e.seats_sold - e.seats_available})`;
                        extraInfo = `(overbooked by ${e.seats_sold - e.seats_available})`;
                    } else if (e.seats_sold === e.seats_available) {
                        seats = ' (sold out)';
                        extraInfo = '(sold out)';
                    } else {
                        seats = ` (${e.seats_available - e.seats_sold} left)`;
                    }
                } else {
                    seats =
                        e.seats_sold >= e.seats_available
                            ? ' (sold out)'
                            : ` (${e.seats_available - e.seats_sold} left)`;
                    extraInfo = e.seats_sold >= e.seats_available ? '(sold out)' : '';
                }
            }

            return {
                ...e,
                title: `${formatMoney(
                    convertToFinalPrice(e.retail_price, e?.final_price)
                )}${seats}`,
                isSeatEvent,
                extraInfo,
                availableSeats,
                activity_start: e.start_time,
                activity_end: e.end_time,
                classNames: e.disabled ? ['disabled'] : []
            };
        });
    }, [timeslotsRaw, user?.role]);

    const { track } = useGA();

    // Track view_item event when activity is loaded
    useEffect(() => {
        if (activity && activity.tickets) {
            const items = [];
            activity.tickets.forEach((ticket, idx) => {
                items.push({
                    index: idx + 1,
                    item_id: `${activity.id.toString()}-${ticket.id.toString()}`,
                    item_name: `${activity.name} | ${ticket.name}`,
                    price: ticket.retail_price || 0,
                    item_category: activity.name
                });
            });

            const value = items.reduce((sum, item) => sum + item.price, 0);

            track.viewItem({
                items,
                value
            });
        }
    }, [activity, track]);

    const handleFillCart = useCallback(
        cartItem => {
            fillCart([withGuestStayDates(cartItem, customer)]);

            // GA tracking - track all tickets in the cart item
            const items = cartItem.tickets.map(ticket => ({
                item_id: `${cartItem.activity_id}-${ticket.id}`,
                item_name: `${cartItem.activity?.name} | ${ticket.name}`,
                price: ticket.ticket_retail_price || ticket.retail_price || 0,
                quantity: ticket.quantity || 1
            }));

            const value = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

            track.addToCart({
                items,
                currency: 'USD',
                value
            });

            // Get booker field defaults from all tickets
            const bookerDefaults = getBookerFieldDefaults(cartItem.tickets);

            // right after fillCart, we can fill in customer data and booker field defaults
            if (customer) {
                const guest = getBookingGuestData(
                    customer,
                    bookerFieldsFromTickets(cartItem.tickets || [])
                );
                updateBookingGuest({ ...bookerDefaults, ...guest });
            } else if (Object.keys(bookerDefaults).length > 0) {
                // Even without customer, set booker field defaults
                updateBookingGuest(bookerDefaults);
            }
            history.push(`/review/last`);
        },
        [customer, fillCart, history, track, updateBookingGuest]
    );

    const handleAddMultipleTickets = useCallback(
        ({ cartItem, availableTickets, timeslot }) => {
            showModal(MODAL_TYPE.MULTIPLE_TICKET, {
                tickets: availableTickets,
                groupSize,
                startTime: timeslot.start_time,
                userRole: user?.role,
                onSubmit: pickedtickets => {
                    // Set the ticket price to the timeslot level calculated ticket price
                    let activityTickets = cartItem.activity.tickets;
                    activityTickets = activityTickets.map(ticketForTimeslot => {
                        const ticketData = availableTickets.find(
                            at => at.id === ticketForTimeslot.id
                        );
                        if (ticketData) {
                            return {
                                ...ticketForTimeslot,
                                retail_price: ticketData.retail_price
                            };
                        }
                        return ticketForTimeslot;
                    });
                    cartItem.activity.tickets = activityTickets;
                    cartItem.tickets = [];
                    pickedtickets.forEach(picked => {
                        const ticket = availableTickets.find(t => t.id === picked.id);
                        if (!ticket) {
                            return;
                        }
                        const quantity = parseInt(picked.value, 10);
                        if (quantity > 0) {
                            cartItem.tickets.push({
                                id: ticket.id,
                                name: ticket.name,
                                booking_fields: ticket.booking_fields,
                                quantity,
                                retail_price: timeslot.retail_price,
                                ticket_retail_price: ticket.retail_price, // store ticket actual retail price too, we need this for correct discount calculate
                                guests: createGuestsWithDefaults(quantity, ticket.booking_fields)
                            });
                        }
                    });
                    // don't add empty ticket listed cartitems to cart
                    if (cartItem.tickets.length === 0) {
                        return;
                    }
                    handleFillCart(cartItem);
                }
            });
        },
        [groupSize, handleFillCart, showModal, user?.role]
    );

    const addToCart = useCallback(
        theTimeslot => {
            // we create fake timeslot in case of custom booking without timeslot (custom timeslot)
            let theTimeslotValue = theTimeslot;
            if (typeof theTimeslot === 'undefined') {
                const fakeTimeslot = {
                    activity_id: activity.id,
                    ticket_id: activity.tickets[0].id,
                    retail_price: activity.tickets[0].retail_price,
                    start_time: moment().format('YYYY-MM-DD HH:mm'),
                    end_time: moment()
                        .add(2, 'hours')
                        .format('YYYY-MM-DD HH:mm'),
                    tickets: activity.tickets
                };
                theTimeslotValue = fakeTimeslot;
            }
            if (!timeslots || !theTimeslotValue) {
                return;
            }

            if (theTimeslotValue.disabled) {
                showModal(MODAL_TYPE.FULLSCREEN_MESSAGE_MODAL, {
                    title: 'Timeslot not available',
                    content: "Please contact your hotel's concierge for last minute availability!",
                    buttonLabel: 'Ok'
                });
                return;
            }

            const timeslot = theTimeslotValue;

            let myTimeslot = timeslots.filter(
                t => t.start_time === timeslot.start_time && t.end_time === timeslot.end_time
            );
            if (myTimeslot.length === 0) {
                // well, this might fix some bugs but also introduce others
                myTimeslot = [theTimeslotValue];
            }

            let availableTickets = myTimeslot
                .map(t => {
                    const ticketdata = activity.tickets.find(({ id }) => id === t.ticket_id);
                    if (!ticketdata) {
                        return null;
                    }
                    return {
                        ...ticketdata,
                        retail_price: t.retail_price,
                        final_price: t.final_price
                    };
                })
                .filter(e => e)
                .sort((a, b) => b?.retail_price - a?.retail_price);

            if (availableTickets.length === 0) {
                return;
            }

            // we need tickets in case of custom booking without timeslot (custom timeslot)
            if (typeof theTimeslot === 'undefined') {
                const availableTicketsWithoutTimeslot = [];
                theTimeslotValue?.tickets?.forEach(t => {
                    const ticketdata = activity.tickets.find(({ id }) => id === t.id);
                    availableTicketsWithoutTimeslot.push({
                        ...ticketdata,
                        retail_price: t.retail_price,
                        final_price: t.final_price
                    });
                });
                availableTickets = availableTicketsWithoutTimeslot;
            }

            // CART ITEM CREATION
            const initialCartItem = {
                // timeslot based data
                activity_id: timeslot.activity_id,
                start_time: timeslot.start_time,
                end_time: timeslot.end_time,
                warning: timeslot.warning,
                // FH availability pk for lodging/pickup selection
                availabilityPk: timeslot.availabilityPk,
                // FK specific
                providerData: timeslot?.providerData || {},
                // everything else for cartItem
                addons: [],
                activity,
                tickets: [],
                agentnote: {}
            };

            if (availableTickets.length > 1) {
                handleAddMultipleTickets({
                    cartItem: initialCartItem,
                    availableTickets,
                    timeslot
                });
                return;
            }

            // add a single ticket, since we only have a single ticket in this activity
            initialCartItem.tickets = availableTickets.map(ticket => {
                return {
                    id: ticket.id,
                    name: ticket.name,
                    booking_fields: ticket.booking_fields,
                    quantity: groupSize,
                    retail_price: timeslot.retail_price,
                    ticket_retail_price: ticket.retail_price, // store ticket actual retail price too, we need this for correct discount calculate
                    guests: createGuestsWithDefaults(groupSize, ticket.booking_fields)
                };
            });

            console.log('Adding to cart single version:', initialCartItem.tickets);
            handleFillCart(initialCartItem);
        },
        [timeslots, activity, groupSize, handleFillCart, showModal, handleAddMultipleTickets]
    );

    // if there is no activity, navigate back to the list
    useEffect(() => {
        if (activityLoading) {
            return;
        }
        if (!activity) {
            onNavigateBack();
        }
    }, [!activity, activityLoading]);

    return !activity ? null : (
        <Grid container wrap="wrap" alignItems="stretch" spacing={2}>
            <Grid item xs={11} style={{ position: 'relative' }}>
                <Link onClick={onNavigateBack}>
                    <IconButton
                        variant="outlined"
                        style={{
                            position: 'absolute',
                            left: '-61px',
                            top: '5px',
                            zIndex: '901',
                            borderColor: '#f6f6f6',
                            color: theme.palette.primary.main,
                            borderWidth: '5px',
                            background: 'white',
                            borderStyle: 'solid'
                        }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                </Link>
                <h1 className="section-h1">{activity?.name}</h1>
            </Grid>
            {user?.role === 'agent' && (
                <div style={{ position: 'absolute', right: 10, top: 1 }}>
                    <ActivityActionButtons
                        showCommission={showCommission}
                        setShowCommission={setShowCommission}
                        showTicketSellingRanges={showTicketSellingRanges}
                        setShowTicketSellingRanges={setShowTicketSellingRanges}
                    />
                </div>
            )}
            <Grid item xs={12}>
                <Grid container wrap="wrap" alignItems="stretch" spacing={2}>
                    <Grid item md={8} xs={12} sm={12} className="activity-slider">
                        <Calendar
                            unique
                            customButtons={{
                                /*
                                findNextButton: {
                                    text: 'Find Next Available Timeslot',
                                    click() {
                                        showModal(MODAL_TYPE.AVAILABLE_TIMESLOTS, {
                                            activity,
                                            addToCart
                                        });
                                    }
                                },*/
                                sync: {
                                    isEnabled: user?.role === 'agent' || user?.role === 'admin',
                                    activity,
                                    timeslots_synced_age: timeslotsRaw?.timeslots_synced_age,
                                    onSyncComplete: reloadTimeslots,
                                    fromDate: calendarWeekStart.format('YYYY-MM-DD'),
                                    toDate: calendarWeekStart
                                        .clone()
                                        .add(7, 'days')
                                        .format('YYYY-MM-DD')
                                },
                                prev: {
                                    click() {
                                        setCalendarWeekStart(start =>
                                            start.clone().subtract(7, 'days')
                                        );
                                    }
                                },
                                next: {
                                    click() {
                                        setCalendarWeekStart(start => start.clone().add(7, 'days'));
                                    }
                                }
                            }}
                            bookings={timeslots}
                            bookingsLoading={timeslotsLoading}
                            onEventClick={event => {
                                addToCart(event);
                            }}
                            firstDay={moment().day()}
                            initialDate={checkInDate}
                        />

                        {activity?.vendor_notes && (
                            <>
                                <br />
                                <h5>Vendor notes to agents</h5>
                                <p style={{ whiteSpace: 'pre-line' }}>{activity?.vendor_notes}</p>
                            </>
                        )}
                        {user?.role === 'agent' && showCommission && (
                            <>
                                <br />
                                <h5>Tickets</h5>
                                <SingleAgentCommissionTable
                                    activity={activity}
                                    timeslots={timeslots}
                                    selectedTimeslot={selectedTimeslot}
                                    showTicketSellingRanges={showTicketSellingRanges}
                                />
                            </>
                        )}
                        {activity?.extra_info?.info && (
                            <>
                                <br />
                                <h5>Additional Information</h5>
                                <Grid
                                    item
                                    xs={12}
                                    style={{
                                        whiteSpace: 'pre-line',
                                        marginTop: 0,
                                        marginBottom: '10px',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    <ReadonlyEditor value={activity?.extra_info?.info} />
                                </Grid>
                            </>
                        )}
                        {activity?.extra_info?.pickup_location && (
                            <>
                                <br />
                                <h5>Pick-Up or Check-In Location</h5>
                                <p style={{ whiteSpace: 'pre-line' }}>
                                    {activity?.extra_info?.pickup_location}
                                </p>
                            </>
                        )}
                        {activity?.extra_info?.before_book && (
                            <>
                                <br />
                                <h5>Know before you book</h5>
                                <Grid
                                    item
                                    xs={12}
                                    style={{
                                        whiteSpace: 'pre-line',
                                        marginTop: 0,
                                        marginBottom: '10px',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    <ReadonlyEditor value={activity?.extra_info?.before_book} />
                                </Grid>
                            </>
                        )}
                        {activity?.extra_info?.before_go && (
                            <>
                                <br />
                                <h5>Know before you go</h5>
                                <Grid
                                    item
                                    xs={12}
                                    style={{
                                        whiteSpace: 'pre-line',
                                        marginTop: 0,
                                        marginBottom: '10px',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    <ReadonlyEditor value={activity?.extra_info?.before_go} />
                                </Grid>
                            </>
                        )}
                        {activity?.extra_info?.vendor_info && (
                            <>
                                <br />
                                <h5>Vendor Information</h5>
                                <Grid
                                    item
                                    xs={12}
                                    style={{
                                        whiteSpace: 'pre-line',
                                        marginTop: 0,
                                        marginBottom: '10px',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    <ReadonlyEditor value={activity?.extra_info?.vendor_info} />
                                </Grid>
                            </>
                        )}
                    </Grid>
                    <Grid item md={4} xs={12} sm={12} className="activity-sidebar">
                        <ImageSlider images={activity?.extra_info?.images} />
                        <br />
                        <h5>{activity?.company?.name}</h5>
                        <p style={{ whiteSpace: 'pre-line' }}>
                            {activity?.company?.contact_phone?.customer}
                        </p>
                        <h5>Description</h5>
                        <Grid
                            item
                            xs={12}
                            style={{
                                whiteSpace: 'pre-line',
                                marginTop: 0,
                                marginBottom: '10px',
                                fontSize: '0.875rem'
                            }}
                        >
                            <ReadonlyEditor value={activity?.description} />
                        </Grid>
                        {activity?.cancellation_window !== 0 && (
                            <>
                                <br />
                                <h5>Cancellation policy</h5>
                                <CancellationWindowText
                                    cancellation_window={activity.cancellation_window}
                                />
                                {activity?.extra_info?.cancellation_info && (
                                    <div
                                        style={{
                                            fontSize: '0.875rem',
                                            marginTop: 0,
                                            marginBottom: '10px'
                                        }}
                                    >
                                        <ReadonlyEditor
                                            value={activity.extra_info.cancellation_info}
                                        />
                                    </div>
                                )}
                                {activity?.extra_info?.terms && (
                                    <p>
                                        <a
                                            href={activity.extra_info.terms}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                color: '#1976d2',
                                                textDecoration: 'underline'
                                            }}
                                        >
                                            Activity terms and conditions
                                        </a>
                                    </p>
                                )}
                            </>
                        )}
                        {activity?.extra_info?.location?.state && (
                            <>
                                <h5>ACTIVITY LOCATION</h5>
                                <p>
                                    {activity?.extra_info?.location?.street},{' '}
                                    {activity?.extra_info?.location?.city},{' '}
                                    {activity?.extra_info?.location?.state}
                                </p>
                            </>
                        )}
                        {!timeslotsLoading && (
                            <ActivityForm
                                addToCart={(start_time, end_time) => {
                                    const timeslot = timeslots.find(
                                        e => e.start_time === start_time && e.end_time === end_time
                                    );
                                    console.log(start_time, end_time, timeslot);
                                    addToCart(timeslot);
                                }}
                                activity={activity}
                                groupSize={groupSize}
                                timeslots={timeslots}
                                checkInDate={calendarWeekStart}
                                selectedTimeslot={selectedTimeslot}
                                selectTimeslot={selectTimeslot}
                                userRole={user?.role}
                            />
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default withTheme(SingleActivity);
